import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  DiscountType,
  OrderLine,
  useLazyGetCartSummaryQuery,
  useEditOrderMutation,
  useLazyGetCartQuery,
  useAddItemToActiveOrderMutation,
  POLineData,
  OrderDataLine,
  useCheckoutOrdersMutation,
  ProductVariant,
  useNotifyToBuyerMutation,
  getOrderPricelist,
  getCatalogConfig,
  useAppSelector,
  dispatch,
  updateOrderPricelist,
  showToast,
  ErrorVariants,
  updateOrderDetailsData,
  OrderLevelScheme,
  useRemoveOrderPromotionMutation,
  useGetOrderLevelSchemesMutation,
  GetOrderLevelSchemesResponse,
} from '@zonofi/zono-money-store';
import { mapPOLineDataToPOLine } from '../../helper';
import { orderHasOutOfStockLine } from '../../helper/is-order-line-out-of-stock';
import { OrderDiscountLevel, OrderProductUpdateType } from '../../constants';
import { toMajorCurrency, toMinorCurrency } from '@zonofi/common';
import { useOrderNote } from '../../order';

interface SellerHubCartProps {
  sellerWorkspaceId: string;
  customerId: string;
}

const buildNotesPayload = (
  orderNote: {
    properties?: Record<string, unknown>;
  },
  values: Record<string, string | null>
) => {
  if (!orderNote?.properties) return undefined;
  const notes: Record<string, string> = {};
  Object.keys(orderNote.properties).forEach((key) => {
    notes[key] = values[key] ?? '';
  });

  return notes;
};

export const useSellerHubCart = ({
  sellerWorkspaceId,
  customerId,
}: SellerHubCartProps) => {
  const [getCartAPI, cartAPIResponse] = useLazyGetCartQuery();
  const [getCartSummaryAPI, cartSummaryAPIResponse] =
    useLazyGetCartSummaryQuery();
  const [getOrderLevelSchemesAPI] = useGetOrderLevelSchemesMutation();
  const [promotionSchemes, setPromotionSchemes] = useState<OrderLevelScheme[]>(
    []
  );
  const [editOrder, editOrderResponse] = useEditOrderMutation();
  const [editScheme] = useEditOrderMutation();
  const [addToCartAPI, addToCartAPIResponse] =
    useAddItemToActiveOrderMutation();
  const [checkoutApi, checkoutApiResponse] = useCheckoutOrdersMutation();
  const [notifyBuyerApi, notifyBuyerApiResponse] = useNotifyToBuyerMutation();
  const { orderNoteFromEditor: orderNote } = useOrderNote();
  const [selectedBenefit, setSelectedBenefit] = useState<OrderLevelScheme>();
  const [showPromotionsModal, setShowPromotionsModal] = useState(false);
  const isChangingBenefitRef = useRef(false);
  const [removeOrderPromotion, removeOrderPromotionResponse] =
    useRemoveOrderPromotionMutation();

  const [showToastSuccess, setShowToastSuccess] = useState({
    show: false,
    msg: '',
  });

  const [showToastError, setShowToastError] = useState({
    show: false,
    msg: '',
  });
  const [removeId, setRemoveId] = useState<number | null>();
  const [advanceRemoveLoading, setAdvanceRemoveLoading] = useState(false);

  const [productsInCart, setProductsInCart] = useState<OrderLine[]>([]);
  const [poFileId, setPoFileId] = useState('');
  const selectedPricelist = useAppSelector(getOrderPricelist);
  const showOutOfStockTag = useAppSelector(getCatalogConfig)?.showOutOfStockTag;
  const onSelectPricelist = (pricelistId: string, pricelistName: string) => {
    dispatch(updateOrderPricelist({ pricelistId, pricelistName }));
  };

  const onSkipPromotion = () => {
    setShowPromotionsModal(false);
  };

  const onCancelPromotion = () => {
    setShowPromotionsModal(false);
  };

  const callCartAPI = () => {
    if (customerId && sellerWorkspaceId) {
      getCartAPI({
        workspaceId: sellerWorkspaceId,
        params: {
          sellerWorkspaceId,
          customerId,
          includeActiveOrders: true,
          includeSummary: true,
        },
      });

      getCartSummaryAPI({
        customerId,
        sellerWorkspaceId,
      });
    }
  };

  const handleUpdate = (item: OrderLine, type: OrderProductUpdateType) => {
    editOrder({
      workspaceId: sellerWorkspaceId,
      body: {
        customerId: customerId ?? '',
        lines: [
          type === OrderProductUpdateType.QUANTITY
            ? {
                quantity: Number(item?.newQnty),
                productVariantId: item?.productVariant?.id,
                discount: Number(item?.lineDiscount?.discount),
                discountType: item?.lineDiscount?.discountType,
              }
            : {
                quantity: Number(item?.quantity ?? 0),
                productVariantId: item?.productVariant?.id,
                listPrice: toMinorCurrency(Number(item?.newPrice ?? 0)),
              },
        ],
        orderId: item.orderId?.toString(),
        poFileId: item.poFileId,
      },
    });
  };

  const orderIds = useMemo(() => {
    const ids =
      cartAPIResponse?.data?.files?.[0]?.lines?.map((x: any) => x?.orderId) ||
      [];

    const uniqueOrderIds: any[] = [
      ...new Set(ids.filter((id: number | null) => id !== null)),
    ];
    return uniqueOrderIds;
  }, [cartAPIResponse]);

  const fetchDiscounts = async () => {
    if (orderIds?.length === 0) return;
    try {
      // call all apis in parallel
      const responses = await Promise.all(
        orderIds.map(
          (id) =>
            id &&
            getOrderLevelSchemesAPI({
              sellerWorkspaceId: sellerWorkspaceId ?? '',
              orderId: Number(id),
              customerId: customerId ?? '',
            }).unwrap()
        )
      );

      const mergedPromotions = Array.from(
        new Map(
          responses
            .flatMap(
              (response: GetOrderLevelSchemesResponse) =>
                response?.promotions || []
            )
            .map((scheme) => [scheme.id, scheme])
        ).values()
      );

      const schemes: OrderLevelScheme[] = mergedPromotions.map(
        (scheme: any) => {
          const promotionCode = scheme?.promotionCode || scheme?.code || '';
          const minOrderQuantity =
            scheme?.minQtyASC ??
            scheme?.minOrderQuantity ??
            scheme?.slab?.[0]?.from;

          return {
            id: scheme?.id,
            name: scheme?.name || 'Promotion',
            code: promotionCode,
            description: scheme?.description || '',
            schemeType:
              scheme?.schemeType || scheme?.promotionType || 'PROMOTION',
            minOrderQuantity,
            schemeIconType: scheme?.schemeIconType,
            productVariantId: scheme?.productVariantId,
          };
        }
      );
      setPromotionSchemes(schemes && schemes.length > 0 ? schemes : []);
    } catch (error) {
      console.log('getOrderLevelSchemesAPI error', error);
      setPromotionSchemes([]);
    }
  };

  const openPromotionsModal = () => {
    setShowPromotionsModal(true);
  };

  const onApplyDiscount = (
    line: OrderDataLine,
    discountType: DiscountType,
    discount: number,
    orderDiscountType: OrderDiscountLevel
  ) => {
    const { quantity, productVariantId } = line;
    const linePayload = { quantity, productVariantId };

    const body =
      orderDiscountType === 'item'
        ? {
            customerId,
            orderId: String(line.orderId),
            lines: [
              {
                ...linePayload,
                discount,
                discountType,
              },
            ],
          }
        : {
            customerId,
            orderId: String(line.orderId),
            discount,
            discountType,
            lines: [linePayload],
          };

    editOrder({
      workspaceId: sellerWorkspaceId,
      body,
    });
  };

  useEffect(() => {
    fetchDiscounts();
  }, [cartAPIResponse]);

  //   line: OrderDataLine,
  //   discountType: DiscountType,
  //   discount: number,
  //   orderDiscountType: OrderDiscountLevel
  // ) => {
  //   const { quantity, productVariantId } = line;
  //   const linePayload = { quantity, productVariantId };

  //   const body =
  //     orderDiscountType === 'item'
  //       ? {
  //           customerId,
  //           orderId: String(line.orderId),
  //           lines: [
  //             {
  //               ...linePayload,
  //               discount,
  //               discountType,
  //             },
  //           ],
  //         }
  //       : {
  //           customerId,
  //           orderId: String(line.orderId),
  //           discount,
  //           discountType,
  //           lines: [linePayload],
  //         };

  //   editOrder({
  //     workspaceId: sellerWorkspaceId,
  //     body,
  //   });
  // };

  const onLineDiscountRemove = (item: OrderLine) => {
    editOrder({
      workspaceId: sellerWorkspaceId,
      body: {
        customerId,
        lines: [
          {
            discount: 0,
            discountType: item?.lineDiscount?.discountType,
            quantity: item?.quantity ?? 0,
            productVariantId: item?.productVariant?.id,
          },
        ],
        orderId: item.orderId?.toString(),
      },
    });
  };

  const onRemovePress = (selectedLine: OrderLine) => {
    setRemoveId(selectedLine?.id);
    if (
      !selectedLine?.productVariant?.id ||
      selectedLine?.productVariant?.id === 0
    ) {
      editOrder({
        workspaceId: sellerWorkspaceId,
        body: {
          customerId,
          lines: [
            {
              quantity: 0,
              poFileLineId: selectedLine?.poFileLineId,
            },
          ],
          poFileId: selectedLine?.poFileId,
        },
      });
    } else {
      editOrder({
        workspaceId: sellerWorkspaceId,
        body: {
          customerId,
          lines: [
            {
              quantity: 0,
              productVariantId: selectedLine?.productVariant?.id,
            },
          ],
          orderId: selectedLine?.orderId,
        },
      });
    }
  };

  useEffect(() => {
    if (cartAPIResponse?.isSuccess && cartAPIResponse?.data) {
      setProductsInCart(() => {
        const lines: POLineData[] = [];
        cartAPIResponse?.data?.files?.forEach((file) => {
          if (file?.lines) {
            lines.push(
              ...(file.lines.map((line: POLineData) => ({
                ...line,
                poFileId: file.id,
              })) || [])
            );
          }
        });

        const cartLines = lines.map(mapPOLineDataToPOLine);
        return cartLines.sort(
          (a, b) => Number(a.sequence) - Number(b.sequence)
        );
      });

      const notes = cartAPIResponse?.data?.notes ?? {};
      dispatch(
        updateOrderDetailsData({
          data: {
            poNumber: cartAPIResponse?.data?.files[0]?.number || '',
            paymentTermsAndConditions: cartAPIResponse?.data?.paymentTerm || '',
            termsAndConditions: cartAPIResponse?.data?.deliveryTerms || '',
            address: cartAPIResponse?.data?.shippingAddress?.streetLine1 || '',
            advancePayment:
              cartAPIResponse?.data?.advancePaymentType === 'percentage'
                ? String(Number(cartAPIResponse?.data?.advancePayment))
                : String(
                    toMajorCurrency(
                      Number(cartAPIResponse?.data?.advancePayment)
                    )
                  ),
            advancePaymentType: cartAPIResponse?.data?.advancePaymentType || '',
            advancePaymentAmount:
              cartAPIResponse?.data?.advancePaymentAmount || '',
            location:
              cartAPIResponse?.data?.shippingAddress?.latitude ||
              cartAPIResponse?.data?.shippingAddress?.longitude
                ? `${cartAPIResponse?.data?.shippingAddress?.latitude || '-'}, ${
                    cartAPIResponse?.data?.shippingAddress?.longitude || '-'
                  }`
                : '',
            ...notes,
          },
          includePaymentLink:
            cartAPIResponse?.data?.includePaymentLink || false,
        })
      );
    }
  }, [cartAPIResponse]);

  useEffect(() => {
    if (
      editOrderResponse.isSuccess ||
      addToCartAPIResponse.isSuccess ||
      checkoutApiResponse.isSuccess
    ) {
      setRemoveId(null);
      callCartAPI();
    }
  }, [editOrderResponse, addToCartAPIResponse, checkoutApiResponse]);

  const onCartCheckout = () => {
    const poFileIds = cartAPIResponse.data?.files.map((file) => file.id);
    if (poFileIds && poFileIds.length > 0) {
      setPoFileId(poFileIds[0]);
    }

    checkoutApi({
      sellerWorkspaceId,
      customerId,
      poFileIds,
    });
  };

  const onReplaceProduct = (pv: ProductVariant, line: OrderLine): void => {
    const isMapped = !!line.productVariant?.productVariantId;

    if (isMapped) {
      // 🔁 Replace mapped product → editOrder
      editOrder({
        workspaceId: sellerWorkspaceId,
        body: {
          customerId,
          lines: [
            {
              productVariantId: pv?.productVariantId,
              quantity: pv?.minOrderQty,
              poFileLineId: line?.poFileLineId,
              orderLineId: line?.id,
            },
          ],
          orderId: line?.orderId,
          poFileId: line?.poFileId,
          ...(selectedPricelist?.pricelistId
            ? { priceListId: selectedPricelist.pricelistId }
            : {}),
        },
      });
      return;
    }

    // ➕ Replace unmapped product → add to cart
    addToCartAPI({
      customerId,
      sellerWorkspaceId,
      source: 'platform',
      poFileId: line.poFileId,
      lines: [
        {
          productVariantId: pv.productVariantId,
          quantity: pv.minOrderQty,
          operator: 'add',
          poFileLineId: line.poFileLineId,
        },
      ],
    });
  };

  const onAskConfirmation = () => {
    if (orderHasOutOfStockLine(productsInCart, !!showOutOfStockTag)) {
      dispatch(
        showToast({
          message:
            'Your cart has out-of-stock items. Replace or remove them before asking for confirmation.',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
      return;
    }

    const poFileId = cartAPIResponse.data?.files?.[0]?.id;

    notifyBuyerApi({
      sellerWorkspaceId,
      poFileId: String(poFileId),
      customerId: String(customerId),
    });
  };

  const onSaveDetails = useCallback(
    (values: Record<string, string | null>, includePaymentLink: boolean) => {
      const orderFile = cartAPIResponse.data?.files?.[0];

      const lines = orderFile?.lines?.map((line) => ({
        orderId: line?.orderId,
        productVariantId: line?.productVariantId,
        quantity: line?.quantity || 1,
        unitPrice: line?.unitPrice,
        customerId,
      }));

      const notes = buildNotesPayload(orderNote, values);
      const [lat, lng] = (values?.location ?? '').split(',');
      const hasAdvancePayment =
        values?.advancePayment != null &&
        values?.advancePayment !== '' &&
        values?.advancePayment !== '0';

      const payload = {
        workspaceId: sellerWorkspaceId!,
        body: {
          customerId: customerId ?? '',
          orderId: Number(orderFile?.lines?.[0]?.orderId),
          lines: lines,
          deliveryTerms: values?.termsAndConditions || '',
          paymentTerm: values?.paymentTermsAndConditions || '',
          advancePayment: hasAdvancePayment
            ? values.advancePaymentType === 'percentage'
              ? Number(values.advancePayment)
              : toMinorCurrency(Number(values.advancePayment))
            : null,
          advancePaymentType: hasAdvancePayment
            ? values.advancePaymentType
            : null,
          includePaymentLink: includePaymentLink,
          poNumber: values?.poNumber || null,
          shippingAddress: {
            latitude: lat ? Number(lat?.trim()) : null,
            longitude: lng ? Number(lng?.trim()) : null,
            streetLine1: values?.address || '',
          },
          ...(orderNote?.properties ? { notes } : {}),
        },
      };

      editOrder(payload)
        .unwrap()
        .then(() => {
          setAdvanceRemoveLoading(false);
          dispatch(
            showToast({
              message: 'Order Details Updated Successfully',
              type: ErrorVariants.SUCCESS,
              canDisplay: true,
              responseStatus: 'success',
            })
          );
        });
    },
    [cartAPIResponse.data]
  );

  const handleRemoveDetails = useCallback(
    (values: Record<string, string>, includePaymentLink: boolean) => {
      setAdvanceRemoveLoading(true);
      const currentValues = {
        ...values,
        advancePayment: null,
        advancePaymentType: null,
      };
      onSaveDetails(
        currentValues as Record<string, string | null>,
        includePaymentLink
      );
    },
    [onSaveDetails]
  );

  useEffect(() => {
    if (cartAPIResponse?.data?.files?.[0]?.lines?.some((x) => x?.promotionId)) {
      const offer = cartAPIResponse?.data?.files?.[0]?.lines?.find(
        (x) => x?.promotionId
      );
      if (offer) {
        setSelectedBenefit({
          id: Number(offer?.promotionId),
          name: offer?.promotionName,
          code: '',
          description: '',
          schemeType: '',
          minOrderQuantity: offer?.MOQ,
          productVariantId: offer?.productVariantId,
        });
      }
    }
  }, [cartAPIResponse]);

  const onApplyPromotion = (
    promotionIds: (string | number)[],
    scheme: OrderLevelScheme
  ) => {
    orderIds?.map(async (orderId) => {
      const selectedOrder = cartAPIResponse?.data?.files?.[0]?.lines?.[0];
      try {
        await editScheme({
          workspaceId: sellerWorkspaceId,
          body: {
            customerId,
            orderId,
            lines: selectedOrder
              ? [
                  {
                    quantity: selectedOrder?.quantity ?? 1,
                    productVariantId: selectedOrder?.productVariantId,
                    ...(typeof selectedOrder?.listPrice === 'number'
                      ? { listPrice: selectedOrder?.linePrice }
                      : {}),
                  },
                ]
              : [],
            promotionIds,
          },
        }).unwrap();
        setSelectedBenefit(scheme);
      } catch {
        return;
      }
    });
  };

  const applyPromotionOnOrder = async (scheme: OrderLevelScheme) => {
    const selectedLine =
      cartAPIResponse?.data?.files?.[0]?.lines?.find(
        (line) => line?.productVariantId === (scheme as any)?.productVariantId
      ) || cartAPIResponse?.data?.files?.[0]?.lines?.[0];
    if (
      selectedLine?.promotionId &&
      Number(selectedLine?.promotionId) !== Number(scheme?.id) &&
      selectedLine?.orderId
    ) {
      isChangingBenefitRef.current = true;
      try {
        await removeOrderPromotion({
          workspaceId: sellerWorkspaceId,
          orderId: Number(selectedLine?.orderId),
          promotionId: Number(selectedLine?.promotionId),
        }).unwrap();
      } catch {
        isChangingBenefitRef.current = false;
        setShowPromotionsModal(false);
        return;
      }
    }
    onApplyPromotion([String(scheme.id)], scheme);
    setShowPromotionsModal(false);
  };

  useEffect(() => {
    if (showToastSuccess?.show) {
      const timeout = setTimeout(() => {
        setShowToastSuccess({
          show: false,
          msg: '',
        });
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [showToastSuccess]);

  useEffect(() => {
    if (removeOrderPromotionResponse?.isSuccess) {
      if (isChangingBenefitRef.current) {
        isChangingBenefitRef.current = false;
        return;
      }
      setSelectedBenefit(undefined);
      callCartAPI();
      setShowToastSuccess({
        show: true,
        msg: 'Benefit removed successfully.',
      });
    } else if (removeOrderPromotionResponse?.isError) {
      if (isChangingBenefitRef.current) {
        isChangingBenefitRef.current = false;
        setShowToastError({
          show: true,
          msg: 'Unable to change benefit. Please try again.',
        });
        return;
      }
      setShowToastError({
        show: true,
        msg: 'Unable to remove benefit. Please try again.',
      });
    }
  }, [removeOrderPromotionResponse]);

  return {
    callCartAPI,
    productsInCart,
    setProductsInCart,
    cartAPIResponse,
    cartSummaryAPIResponse,
    editOrderResponse,
    handleUpdate,
    onApplyDiscount,
    onRemovePress,
    onLineDiscountRemove,
    addToCartAPI,
    addToCartAPIResponse,
    orderDiscount: cartAPIResponse.data?.files[0]?.discount,
    onCartCheckout,
    checkoutApiResponse,
    onReplaceProduct,
    poFileId,
    onAskConfirmation,
    confirmationLoading: notifyBuyerApiResponse?.isLoading,
    selectedPricelist,
    onSelectPricelist,
    onSaveDetails,
    savingDetailsLoader: editOrderResponse?.isLoading,
    selectedBenefit,
    setSelectedBenefit,
    showPromotionsModal,
    setShowPromotionsModal,
    applyPromotionOnOrder,
    promotionSchemes,
    onApplyPromotion,
    onSkipPromotion,
    onCancelPromotion,
    openPromotionsModal,
    removeId,
    handleRemove: handleRemoveDetails,
    advanceRemoveLoading,
  };
};
